import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LEGAL_POLICY_VERSION } from '../legal/policyVersion';
import { recordLegalAcceptance } from '../legal/acceptance';
import { authService } from '../services/api';
import { hasActiveSession } from '../utils/session';
import { LegalConsentCheckbox } from '../components/LegalConsentCheckbox';
import { OutrisSiteFooter } from '../components/OutrisSiteFooter';

export function AcceptLegal() {
    const [agreed, setAgreed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverChecked, setServerChecked] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/';
    const hasSession = hasActiveSession();

    useEffect(() => {
        if (!hasSession) return;
        let cancelled = false;
        authService.getMe()
            .then((me: { legal_version?: string | null; legal_accepted_at?: string | null }) => {
                if (cancelled) return;
                if (me?.legal_version === LEGAL_POLICY_VERSION && me?.legal_accepted_at) {
                    recordLegalAcceptance();
                    navigate(from, { replace: true });
                    return;
                }
                setServerChecked(true);
            })
            .catch(() => {
                if (!cancelled) setServerChecked(true);
            });
        return () => { cancelled = true; };
    }, [hasSession, from, navigate]);

    if (!hasSession) {
        return <Navigate to="/login" replace />;
    }

    if (!serverChecked) {
        return (
            <div className="min-h-screen bg-brand-page dark:bg-background" data-testid="legal-server-sync" />
        );
    }

    const onContinue = async () => {
        if (!agreed) return;
        setSaving(true);
        try {
            await authService.acceptLegal(LEGAL_POLICY_VERSION);
            recordLegalAcceptance();
            navigate(from, { replace: true });
        } catch {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-brand-page dark:bg-background flex flex-col" data-testid="legal-accept-page">
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="w-full max-w-lg rounded-xl border border-border bg-card p-8">
                    <h1 className="text-xl font-bold text-foreground">Confirm what we collect</h1>
                    <p className="mt-2 text-sm text-muted-foreground" data-testid="legal-accept-scope">
                        These are CryptoFlow&apos;s law-enforcement Terms of Use, separate from the Outris
                        platform terms accepted at portal sign-up; we ask once for each version.
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Review the Privacy Policy and Terms of Use (version {LEGAL_POLICY_VERSION}):
                        scroll to the bottom of both documents, then confirm.
                    </p>
                    <div className="mt-4">
                        <LegalConsentCheckbox
                            agreed={agreed}
                            onChange={setAgreed}
                            checkboxTestId="legal-accept-checkbox"
                        />
                    </div>
                    <button
                        type="button"
                        disabled={!agreed || saving}
                        onClick={onContinue}
                        className="mt-6 w-full rounded-lg bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-50"
                        data-testid="legal-accept-continue"
                    >
                        Continue
                    </button>
                </div>
            </div>
            <OutrisSiteFooter />
        </div>
    );
}
